import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { JobType } from "../../../../../../../models/jobType";
import { Searches, Filters, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import JobTypeService from "../../../../../../../services/job.type.service";

const useActions = () => {
  const { currentUser } = useAppState();

  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["job.type.list", page, pageSize, filters, searches, sorting],
    () =>
      JobTypeService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "groups" }]),
        sortBy: sorting
          ? JSON.stringify([...sorting, ["name", "asc"]])
          : JSON.stringify([["name", "asc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setJobTypes(response.data);
      },
    }
  );

  const { refetch: getJobTypeTotal } = useQuery(
    ["job.type.count", filters, searches],
    () =>
      JobTypeService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: createJobType, isLoading: isCreating } = useMutation(
    ["job.type.create"],
    (jobType: JobType) => JobTypeService.create(jobType),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getJobTypeTotal();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateJobType, isLoading: isUpdating } = useMutation(
    ["job.type.update"],
    ({ id, jobType }: { id: number; jobType: JobType }) => JobTypeService.update(id, jobType),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        getJobTypeTotal();
      },
      onError: (err: any) => {
        NotificationToast.error(err.response.data);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    jobTypes,
    hasErrors,
    page,
    total,
    pageSize,
    isLoading: isFetching || isCreating || isUpdating,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createJobType,
    updateJobType,
  };
};

export default useActions;
